// Общие элементы панели администрирования: даты, объём, полоса занятости, сортируемый заголовок таблицы,
// копирование ссылки, окна «показать один раз» и «подтвердите вводом логина».
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { DialogProps } from '../../core/registry';
import { ctx } from '../../core/ctx';
import { Btn, Modal } from '../../ui/controls';
import { Icon } from '../../ui/icons';
import { fmtBytes } from '../auth/session';

export { fmtBytes };

export function fmtDate(ts: number | null | undefined, withYear = false): string {
  if (!ts) return '—';
  const d = new Date(ts * 1000);
  const now = new Date();
  return d.toLocaleString('ru-RU', { day: 'numeric', month: 'short', year: withYear || d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    hour: '2-digit', minute: '2-digit' });
}

export function fmtAgo(ts: number | null | undefined): string {
  if (!ts) return 'никогда';
  const s = Date.now() / 1000 - ts;
  if (s < 60) return 'только что';
  if (s < 3600) return `${Math.floor(s / 60)} мин назад`;
  if (s < 86400) return `${Math.floor(s / 3600)} ч назад`;
  if (s < 86400 * 7) return `${Math.floor(s / 86400)} дн. назад`;
  return fmtDate(ts);
}

/** Срок в будущем: «через 3 дн.», «истекло». */
export function fmtLeft(ts: number | null | undefined): string {
  if (!ts) return 'бессрочно';
  const s = ts - Date.now() / 1000;
  if (s <= 0) return 'истекло';
  if (s < 3600) return `через ${Math.max(1, Math.round(s / 60))} мин`;
  if (s < 86400) return `через ${Math.round(s / 3600)} ч`;
  return `через ${Math.round(s / 86400)} дн.`;
}

export function isoToTs(s: string | undefined): number {
  const t = Date.parse(s || '');
  return isNaN(t) ? 0 : t / 1000;
}

export function UsageBar({ used, total, wide }: { used: number; total: number | null; wide?: boolean }) {
  if (!total) return <span className="adm-usage"><span className="adm-usage-text">{fmtBytes(used)}</span></span>;
  const pct = Math.min(100, (used / total) * 100);
  return (
    <span className={'adm-usage' + (wide ? ' is-wide' : '')} title={`${fmtBytes(used)} из ${fmtBytes(total)} (${Math.round(pct)}%)`}>
      <span className={'adm-bar' + (pct > 90 ? ' is-full' : pct > 70 ? ' is-high' : '')}><span style={{ width: Math.max(pct, used ? 2 : 0) + '%' }} /></span>
      <span className="adm-usage-text">{fmtBytes(used)} <span className="adm-dim">/ {fmtBytes(total)}</span></span>
    </span>
  );
}

export type Sort<K extends string> = { key: K; dir: 1 | -1 };
export function SortTh<K extends string>({ k, sort, setSort, children, className }: {
  k: K; sort: Sort<K>; setSort: (s: Sort<K>) => void; children: ReactNode; className?: string;
}) {
  const active = sort.key === k;
  return (
    <th className={className} aria-sort={active ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className={'adm-th' + (active ? ' is-active' : '')} onClick={() => setSort({ key: k, dir: active ? (sort.dir === 1 ? -1 : 1) : 1 })}
        data-testid={'sort-' + k}>
        {children}<Icon name={active ? (sort.dir === 1 ? 'up' : 'arrow-down') : 'up-down'} size={12} />
      </button>
    </th>
  );
}

/** Загрузка данных вкладки: {data, error, loading, reload}. */
export function useLoad<T>(fn: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const seq = useRef(0);
  const reload = useCallback(async () => {
    const my = ++seq.current;
    setLoading(true);
    try {
      const d = await fn();
      if (my === seq.current) { setData(d); setError(''); }
    } catch (e: any) {
      if (my === seq.current) setError(e?.message || String(e));
    } finally {
      if (my === seq.current) setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => { void reload(); }, [reload]);
  return { data, error, loading, reload, setData };
}

/** Выполнить действие с сообщением об успехе/ошибке и перезагрузкой. */
export async function act(fn: () => Promise<unknown>, ok?: string, after?: () => unknown) {
  try {
    await fn();
    if (ok) ctx.toast(ok, 'ok');
    await after?.();
    return true;
  } catch (e: any) {
    ctx.toast(e?.message || String(e), 'error', 8000);
    return false;
  }
}

/** Скопировать текст (буфер обмена браузера; без HTTPS - запасной способ). */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(text); return true; }
  } catch { /* ниже - запасной способ */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  } catch { return false; }
}

export function CopyField({ value, testid, label = 'Копировать' }: { value: string; testid?: string; label?: string }) {
  const [done, setDone] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  const copy = async () => {
    const ok = await copyText(value);
    if (ok) { setDone(true); setTimeout(() => setDone(false), 1800); } else {
      ref.current?.select();
      ctx.toast('Не удалось скопировать автоматически - выделено, нажмите Ctrl+C', 'warn');
    }
  };
  return (
    <div className="adm-copy">
      <input ref={ref} readOnly value={value} onFocus={(e) => e.target.select()} data-testid={testid} aria-label="Ссылка" />
      <Btn icon={done ? 'ok' : 'copy'} label={done ? 'Скопировано' : label} primary={!done} onClick={copy} testid={testid ? testid + '-copy' : undefined} />
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ окна
export interface SecretProps { title: string; text: ReactNode; secret: string; label?: string; note?: string; testid?: string }
/** Показать секрет (временный пароль, ссылку) один раз - с копированием. */
export function SecretDialog({ props, close }: DialogProps) {
  const o = props as SecretProps;
  return (
    <Modal title={o.title} onClose={() => close()} width={520} testid={o.testid || 'secret-dialog'} footer={<Btn label="Готово" primary onClick={() => close()} testid="secret-done" />}>
      <div className="adm-secret">
        <div className="adm-secret-text">{o.text}</div>
        <div className="adm-secret-label">{o.label || 'Временный пароль'}</div>
        <CopyField value={o.secret} testid="secret-value" />
        {o.note && <p className="adm-note"><Icon name="info" size={14} /> {o.note}</p>}
      </div>
    </Modal>
  );
}

export interface TypedConfirmProps { title: string; text: ReactNode; word: string; ok: string; testid?: string }
/** Опасное действие: подтвердить вводом слова (логина пользователя). */
export function TypedConfirmDialog({ props, close }: DialogProps<boolean>) {
  const o = props as TypedConfirmProps;
  const [v, setV] = useState('');
  const ok = v.trim().toLowerCase() === o.word.toLowerCase();
  return (
    <Modal title={o.title} onClose={() => close()} width={480} testid={o.testid || 'typed-confirm'} footer={<>
      <Btn label="Отмена" onClick={() => close()} testid="typed-cancel" />
      <button type="button" className="btn btn-danger-solid" disabled={!ok} onClick={() => close(true)} data-testid="typed-ok">{o.ok}</button>
    </>}>
      <div className="adm-danger-text">{o.text}</div>
      <label className="field field-wide adm-typed">
        <span className="field-label">Для подтверждения введите <b className="mono">{o.word}</b></span>
        <input value={v} onChange={(e) => setV(e.target.value)} data-testid="typed-input" autoComplete="off" spellCheck={false}
          onKeyDown={(e) => { if (e.key === 'Enter' && ok) close(true); }} />
      </label>
    </Modal>
  );
}

export const askTyped = (o: TypedConfirmProps) => ctx.openDialog<boolean>('admin.typedConfirm', o as unknown as Record<string, unknown>);
export const showSecret = (o: SecretProps) => ctx.openDialog('admin.secret', o as unknown as Record<string, unknown>);

export function Pill({ tone, children, testid }: { tone: 'ok' | 'warn' | 'err' | 'info' | 'dim'; children: ReactNode; testid?: string }) {
  return <span className={'adm-pill adm-pill-' + tone} data-testid={testid}>{children}</span>;
}

export function LoadError({ error, reload }: { error: string; reload: () => void }) {
  if (!error) return null;
  return <div className="adm-error" role="alert"><Icon name="error" size={16} /><span>{error}</span><Btn label="Повторить" onClick={reload} /></div>;
}

export function RowMenuButton({ onOpen, testid, label = 'Действия' }: { onOpen: (el: HTMLElement) => void; testid?: string; label?: string }) {
  return (
    <button type="button" className="adm-more" aria-label={label} data-tip={label} data-testid={testid}
      onClick={(e) => onOpen(e.currentTarget)}><Icon name="more" size={16} /></button>
  );
}
